import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CompanyProvider } from "./context/CompanyContext";
import { Toaster } from "./components/ui/sonner";
import ChatWidget from "./components/ChatWidget";

// Pages
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import NewTicket from "./pages/NewTicket";
import BatchImport from "./pages/BatchImport";
import UserManagement from "./pages/UserManagement";
import ActivityLog from "./pages/ActivityLog";
import CompanyManagement from "./pages/CompanyManagement";
import RawMaterials from "./pages/RawMaterials";
import InvoiceUpload from "./pages/InvoiceUpload";
import Sales from "./pages/Sales";
import SupplierSamples from "./pages/SupplierSamples";
import Reports from "./pages/Reports";
import ProductManagement from "./pages/ProductManagement";
import PendingApproval from "./pages/PendingApproval";

// Helper: user is fully approved and active
const isApproved = (u) => {
  if (!u) return false;
  const status = u.status ?? 'approved'; // backward compatibility
  const active = u.is_active ?? true;
  return status === 'approved' && active === true;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data passed from AuthCallback, use it
  if (location.state?.user) {
    const passedUser = location.state.user;
    if (!isApproved(passedUser)) {
      return <Navigate to="/pending-approval" replace />;
    }
    return <CompanyProvider>{children}<ChatWidget /></CompanyProvider>;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isApproved(user)) {
    return <Navigate to="/pending-approval" replace />;
  }

  return <CompanyProvider>{children}<ChatWidget /></CompanyProvider>;
};

// Pending route: only accessible when user is authenticated but not yet approved
const PendingRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (isApproved(user)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Router Component with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id - BEFORE routes render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/pending-approval"
        element={
          <PendingRoute>
            <PendingApproval />
          </PendingRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <Tickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/new"
        element={
          <ProtectedRoute>
            <NewTicket />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/:ticketId"
        element={
          <ProtectedRoute>
            <TicketDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <BatchImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <ActivityLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <CompanyManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/raw-materials"
        element={
          <ProtectedRoute>
            <RawMaterials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoiceUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-samples"
        element={
          <ProtectedRoute>
            <SupplierSamples />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductManagement />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
